/**
 * Where to send someone after they sign in.
 *
 * ## Why this exists at all
 *
 * `_authenticated` throws `redirect({ to: "/auth", search: { redirect:
 * location.href } })` when there is no session, and the sign-in page never read
 * that param — so every sign-in landed on `/events` regardless of where the
 * person was heading. Mostly invisible, because `/events` is usually where they
 * were going anyway.
 *
 * Not invisible for `/cli-login`, which carries the login code and callback
 * port in its query string. Being bounced to `/events` drops both, the page
 * then reports it "needs a code and a port", and the CLI waits for a callback
 * that never arrives.
 *
 * ## Same-origin paths only
 *
 * Honouring an arbitrary URL here would make the sign-in page an open redirect,
 * which is the most convincing place in the whole app to have one: the person
 * has just typed their password, and "sign in and get sent onward" is exactly
 * what they expect to happen.
 *
 * So: a path, starting with exactly one slash. The second character matters as
 * much as the first — `//evil.example` is protocol-relative and navigates
 * off-origin, and checking only `startsWith("/")` is the usual way this
 * validation is written wrong. A backslash is refused for the same reason,
 * since browsers have historically normalised `\\` to `//`.
 */
export function safeRedirect(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length === 0) return undefined;

  if (!value.startsWith("/")) return undefined;
  if (value.startsWith("//") || value.startsWith("/\\")) return undefined;

  return value;
}
